
import React, { useEffect, useState } from 'react';
import { Activity, AlertTriangle, Info, ShieldAlert, Download, Filter, X } from 'lucide-react';
import { Button, Select, Input } from '@/shared/components/ui';
import { adminServiceAPI } from '@/features/admin/services/adminService-api';

const ACTION_TYPE_OPTIONS = [
  { value: '', label: 'All Action Types' },
  { value: 'user_management', label: 'User Management' },
  { value: 'system_config', label: 'System Configuration' },
  { value: 'authentication', label: 'Authentication' },
  { value: 'data_modification', label: 'Data Modification' },
  { value: 'other', label: 'Other' },
];

const SEVERITY_OPTIONS = [
  { value: '', label: 'All Severities' },
  { value: 'info', label: 'Info' },
  { value: 'warning', label: 'Warning' },
  { value: 'critical', label: 'Critical' },
];

const AdminAuditView: React.FC = () => {
  const [logs, setLogs] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    actionType: '',
    severity: '',
    startDate: '',
    endDate: '',
  });
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    loadAuditLogs();
  }, [filters]);

  const loadAuditLogs = async () => {
    setLoading(true);
    try {
      // Use adminServiceAPI to fetch real audit logs from database
      const auditLogs = await adminServiceAPI.getAuditLogs(
        Object.fromEntries(
          Object.entries({
            actionType: filters.actionType,
            severity: filters.severity,
            startDate: filters.startDate,
            endDate: filters.endDate,
          }).filter(([_, v]) => v !== undefined && v !== '')
        )
      );

      setLogs(auditLogs);
    } catch (error) {
      console.error('Failed to load audit logs:', error);
      setLogs([]);
    }
    setLoading(false);
  };

  const handleExport = async () => {
    try {
      // Fetch audit logs with current filters
      const auditLogs = await adminServiceAPI.getAuditLogs(
        Object.fromEntries(
          Object.entries({
            actionType: filters.actionType,
            severity: filters.severity,
            startDate: filters.startDate,
            endDate: filters.endDate,
          }).filter(([_, v]) => v !== undefined && v !== '')
        )
      );

      // Create CSV content
      const headers = ['Timestamp', 'Action', 'Action Type', 'Details', 'Target User', 'Admin Name', 'Admin Email', 'Severity'];
      const rows = auditLogs.map(log => [
        new Date(log.timestamp).toLocaleString(),
        log.action,
        log.actionType || 'N/A',
        log.details,
        log.userId ? log.userId.substring(0, 8) + '...' : 'N/A',
        log.adminUserName || 'N/A',
        log.adminUserEmail || 'N/A',
        log.severity,
      ]);

      const csvContent = [
        headers.join(','),
        ...rows.map(row => row.map(cell => `"${cell}"`).join(',')),
      ].join('\n');

      // Create download link
      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `audit-logs-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to export audit logs:', error);
      alert('Failed to export audit logs. Please try again.');
    }
  };

  const clearFilters = () => {
    setFilters({
      actionType: '',
      severity: '',
      startDate: '',
      endDate: '',
    });
  };

  const hasActiveFilters = Object.values(filters).some(value => value !== '');

  const getIcon = (severity: string) => {
    switch(severity) {
      case 'critical': return <ShieldAlert size={14} className="text-error-600"/>;
      case 'warning': return <AlertTriangle size={14} className="text-warning-600"/>;
      default: return <Info size={14} className="text-info-600"/>;
    }
  };

  const getSeverityBadgeClass = (severity: string) => {
    switch(severity) {
      case 'critical': return 'bg-error-50 border-error-100 text-error-700';
      case 'warning': return 'bg-warning-50 border-warning-100 text-warning-700';
      default: return 'bg-info-50 border-info-100 text-info-700';
    }
  };

  const getActionTypeBadgeClass = (actionType?: string) => {
    switch(actionType) {
      case 'user_management': return 'bg-primary-50 border-primary-100 text-primary-700';
      case 'system_config': return 'bg-warning-50 border-warning-100 text-warning-700';
      case 'authentication': return 'bg-error-50 border-error-100 text-error-700';
      case 'data_modification': return 'bg-purple-50 border-purple-100 text-purple-700';
      default: return 'bg-gray-50 border-gray-100 text-gray-600';
    }
  };

  return (
    <div className="space-y-5 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-primary-100 rounded text-primary-700">
            <Activity size={16} />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Audit Logs</h2>
            <p className="text-gray-500 text-sm">Track all admin actions and system events.</p>
          </div>
        </div>
        <Button
          variant="outline"
          onClick={handleExport}
          disabled={logs.length === 0}
          leftIcon={<Download size={14} />}
        >
          Export CSV
        </Button>
      </div>

      {/* Filters */}
      <div className="bg-white p-4 rounded border border-gray-200">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Filter size={14} className="text-gray-500" />
            <h3 className="font-semibold text-gray-900 text-sm">Filters</h3>
            {hasActiveFilters && (
              <span className="bg-primary-100 text-primary-700 px-2 py-0.5 rounded text-xs font-medium">
                Active
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {hasActiveFilters && (
              <Button
                variant="outline"
                size="sm"
                onClick={clearFilters}
                leftIcon={<X size={14} />}
              >
                Clear All
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowFilters(!showFilters)}
            >
              {showFilters ? 'Hide' : 'Show'} Filters
            </Button>
          </div>
        </div>

        {showFilters && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-500 uppercase mb-1">Action Type</label>
              <Select
                options={ACTION_TYPE_OPTIONS}
                value={filters.actionType}
                onChange={(e) => setFilters(prev => ({ ...prev, actionType: e.target.value }))}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 uppercase mb-1">Severity</label>
              <Select
                options={SEVERITY_OPTIONS}
                value={filters.severity}
                onChange={(e) => setFilters(prev => ({ ...prev, severity: e.target.value }))}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 uppercase mb-1">Start Date</label>
              <Input
                type="date"
                value={filters.startDate}
                onChange={(e) => setFilters(prev => ({ ...prev, startDate: e.target.value }))}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 uppercase mb-1">End Date</label>
              <Input
                type="date"
                value={filters.endDate}
                onChange={(e) => setFilters(prev => ({ ...prev, endDate: e.target.value }))}
              />
            </div>
          </div>
        )}
      </div>

      {/* Results Count */}
      <div className="text-sm text-gray-500">
        {loading ? (
          <span>Loading logs...</span>
        ) : logs.length === 0 ? (
          <span>No logs found</span>
        ) : (
          <span>
            Showing <span className="font-semibold text-gray-900">{logs.length}</span> log{logs.length !== 1 ? 's' : ''}
          </span>
        )}
      </div>

      {/* Logs Table */}
      <div className="bg-white rounded border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="bg-gray-50 border-b border-gray-200 text-xs font-medium text-gray-500 uppercase">
              <tr>
                <th className="px-4 py-3">Severity</th>
                <th className="px-4 py-3">Action</th>
                <th className="px-4 py-3">Type</th>
                <th className="px-4 py-3">Details</th>
                <th className="px-4 py-3">Target User</th>
                <th className="px-4 py-3">Admin</th>
                <th className="px-4 py-3 text-right">Timestamp</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr><td colSpan={7} className="p-8 text-center text-gray-400">Loading logs...</td></tr>
              ) : logs.length === 0 ? (
                <tr><td colSpan={7} className="p-8 text-center text-gray-400 italic text-sm">No logs recorded yet.</td></tr>
              ) : (
                logs.map(log => (
                  <tr key={log.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-xs font-medium border ${getSeverityBadgeClass(log.severity)}`}>
                        {getIcon(log.severity)}
                        <span className="uppercase">{log.severity}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 font-medium text-gray-900">{log.action}</td>
                    <td className="px-4 py-3">
                      {log.actionType && (
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border ${getActionTypeBadgeClass(log.actionType)}`}>
                          {log.actionType.replace('_', ' ')}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-gray-600 max-w-md truncate" title={log.details}>
                      {log.details}
                    </td>
                    <td className="px-4 py-3 text-gray-500 text-xs">
                      {log.userId ? (
                        <div>
                          <div className="font-mono">{log.userId.substring(0, 8)}...</div>
                        </div>
                      ) : (
                        <span className="italic">N/A</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-sm">
                        <div className="font-medium text-gray-900">{log.adminUserName || 'Unknown'}</div>
                        <div className="text-xs text-gray-500">{log.adminUserEmail || ''}</div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right text-gray-500 text-xs">
                      {new Date(log.timestamp).toLocaleString()}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AdminAuditView;
